import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { setAccessToken } from "../api";
import { authApi } from "../services/auth";
import FeedbackBoardSheet from "../components/FeedbackBoardSheet";
import ShareFeedbackSheet from "../components/ShareFeedbackSheet";
import { usersApi } from "../services/users";
import { openExternal } from "../lib/openExternal";
import { markExplicitSignOut } from "../lib/biometricSignIn";
import { INSTAGRAM_URL } from "../constants/links";
import { FaChevronRight } from "react-icons/fa";
import BottomSheet from "../components/BottomSheet";
import LanguageSheet, { currentLanguageName, languagePickerAvailable } from "../components/LanguageSheet";
import "./MorePage.css";
import { t } from "../i18n";
import { T } from "../i18n/T";

// Contact Us opens the user's mail app to our monitored primary inbox. If we
// later want support isolated, point this at a support@ Workspace alias.
const CONTACT_EMAIL = "hello@requesta.app";

// Headphones — "Contact us" (the sheet's own header carries no icon: two
// headphones in one sheet looked odd — founder).
const HEADPHONES = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 14v-2a8 8 0 0 1 16 0v2" /><rect x="2.5" y="13.5" width="4.5" height="6.5" rx="2.2" /><rect x="17" y="13.5" width="4.5" height="6.5" rx="2.2" />
  </svg>
);
const TRASH = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7h16"/><path d="M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2"/><path d="M6 7l1 12a2 2 0 002 2h6a2 2 0 002-2l1-12"/></svg>
);

const ChevronRow: React.FC = () => (
  <FaChevronRight className="profile-menu-chev" aria-hidden />
);

// The Profile drawer's footer + its sheets (build 38): the Instagram line, the
// "Help, terms & support" chooser (opened from Settings), Share Feedback (the
// chooser's Help Center) and Terms (Terms / Privacy / Delete account — the old
// "Boring Stuff"). Log out moved up under Settings (ProfilePage). Delete still
// runs the exact same 7-day grace flow the Profile page used. (Leaderboard
// was promoted to the top of the Profile menu; Request History moved up
// earlier; the in-app Support/ChatBot row was retired.)
//
// Since the tab consolidation this renders EMBEDDED inside the Profile
// page (beneath Preferences) rather than as its own tab — pass `embedded` to
// drop the standalone page heading + full-page container. The standalone /more
// route now redirects to /profile (see App.tsx), so `embedded` is the live path.
const MorePage: React.FC<{ embedded?: boolean; openHelpSignal?: number; onHelpClosed?: () => void; onLogOut?: () => void }> = ({ embedded = false, openHelpSignal = 0, onHelpClosed, onLogOut }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [delOpen, setDelOpen] = useState(false);
  // "Terms" drawer (was "Boring Stuff") — Terms / Privacy / Delete account.
  const [termsOpen, setTermsOpen] = useState(false);
  // "Help, terms & support" chooser — opened from Settings.
  const [helpOpen, setHelpOpen] = useState(false);
  // Feedback board (1.5.1): the chooser's first door; "Share an idea" on it
  // opens the Share Feedback form below.
  const [boardOpen, setBoardOpen] = useState(false);
  // Language (founder, 1.5.2 b45): its own quiet pill above the footer line,
  // one tap from the drawer instead of two deep in Settings.
  const [langOpen, setLangOpen] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  const [deletionInfo, setDeletionInfo] = useState<{
    status?: "active" | "pending_delete" | "deleted";
    scheduled?: string | null;
  }>({});

  // Deletion status drives Delete-vs-Cancel. Same source the Profile page read.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    usersApi
      .get(user.slug)
      .then((res) => {
        if (cancelled) return;
        const d = (res || {}) as any;
        setDeletionInfo({ status: d.status || "active", scheduled: d.deletionScheduledFor || null });
      })
      .catch(() => { /* non-fatal — defaults to "active" */ });
    return () => { cancelled = true; };
  }, [user]);

  const requestAccountDeletion = async () => {
    if (!user) return;
    try {
      setDelBusy(true);
      const res = await usersApi.requestDeletion(user.slug);
      const scheduled = res?.deletionScheduledFor;
      setDeletionInfo({ status: "pending_delete", scheduled });
      setDelOpen(false);
      alert(t("morePage.yourAccountIsScheduled"));
      // Deliberate sign-out: keep the Face ID auto-prompt quiet on the login page.
      markExplicitSignOut();
      await authApi.logout();
      setAccessToken(null);
      navigate("/login", { replace: true });
    } catch (e) {
      console.error(e);
      alert(t("morePage.failedToScheduleDeletion"));
    } finally {
      setDelBusy(false);
    }
  };

  const cancelAccountDeletion = async () => {
    if (!user) return;
    try {
      setDelBusy(true);
      await usersApi.cancelDeletion(user.slug);
      setDeletionInfo({ status: "active", scheduled: null });
      alert(t("morePage.deletionWasCanceledYour"));
    } catch (e) {
      console.error(e);
      alert(t("morePage.failedToCancelDeletion"));
    } finally {
      setDelBusy(false);
    }
  };

  // ── Share Feedback (build 44): opened first from Help, terms & support,
  // or from the board's own share button. Closing goes back to whichever
  // sheet opened it; "See the Feedback board" swaps to the board.
  const [fbOpen, setFbOpen] = useState(false);
  const [fbFrom, setFbFrom] = useState<"help" | "board">("help");

  const openFeedback = (from: "help" | "board") => {
    setFbFrom(from);
    setFbOpen(true);
  };

  // Bumped from outside (Settings → "Help, terms & support") to open the chooser.
  useEffect(() => {
    if (openHelpSignal) setHelpOpen(true);
  }, [openHelpSignal]);

  const closeFeedback = () => {
    setFbOpen(false);
    if (fbFrom === "board") setBoardOpen(true);
    else setHelpOpen(true);
  };

  const seeBoard = () => {
    setFbOpen(false);
    setBoardOpen(true);
  };

  const contactHref = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
    `Contact — Request App${user?.name ? " · " + user.name : ""}`
  )}`;

  return (
    <div className={embedded ? "more-embedded" : "more-page"}>
      {!embedded && (
        <h2 className="profile-title" style={{ marginBottom: 14 }}>{t("morePage.more")}</h2>
      )}

      {/* Language, centred right above the footer line (founder, 1.5.2 b45:
          "more accessible"). The globe + the language's own name read in any
          language, so a talent who switched by mistake can always find the
          way back. */}
      {languagePickerAvailable() && (
        <button
          type="button"
          className="more-lang"
          onClick={() => setLangOpen(true)}
          aria-label={`${t("profileSettingsSheet.language")}: ${currentLanguageName()}`}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M3 12h18" />
            <path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18Z" />
          </svg>
          {currentLanguageName()}
          <FaChevronRight className="more-lang-chev" aria-hidden />
        </button>
      )}
      {/* The last line on screen: Instagram, and Log out beside it in quiet
          grey (founder, 1.5.2: a full menu row drew too much attention). */}
      <div className="more-foot">
        <button type="button" className="profile-ig-pill" onClick={() => openExternal(INSTAGRAM_URL)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#e1306c" strokeWidth="2.2" aria-hidden>
            <rect x="3.5" y="3.5" width="17" height="17" rx="5" />
            <circle cx="12" cy="12" r="3.8" />
            <circle cx="17" cy="7" r="0.7" fill="#e1306c" />
          </svg>
          {t("morePage.followUsOnInstagram")}
        </button>
        {onLogOut && (
          <>
            <span className="more-foot-dot" aria-hidden="true">·</span>
            <button type="button" className="more-logout" onClick={onLogOut}>
              {t("profilePage.logOut")}
            </button>
          </>
        )}
      </div>

      <LanguageSheet open={langOpen} onClose={() => setLangOpen(false)} />

      <FeedbackBoardSheet
        open={boardOpen}
        onClose={() => {
          setBoardOpen(false);
          setHelpOpen(true); // back one level, to the help chooser
        }}
        onShareIdea={() => {
          setBoardOpen(false);
          openFeedback("board");
        }}
      />

      <ShareFeedbackSheet open={fbOpen} onClose={closeFeedback} onSeeBoard={seeBoard} />

      {/* ── "Help, terms & support" (founder, round 4): four doors — feedback,
          the legal pair, an email to us, and the account deletion. ── */}
      <BottomSheet
        open={helpOpen}
        title={t("morePage.helpTermsSupport")}
        onClose={() => {
          // Back one level: the chooser was opened from Settings (founder).
          setHelpOpen(false);
          onHelpClosed?.();
        }}
      >
        <div className="profile-menu">
          <button
            type="button"
            className="profile-menu-row"
            onClick={() => {
              // Share Feedback opens the form first; the board is one tap from it (founder, build 44).
              setHelpOpen(false);
              openFeedback("help");
            }}
          >
            <span className="profile-menu-ic" aria-hidden="true">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 5h16v11H9l-5 4z"/><path d="M8 9h8M8 12.5h5"/></svg>
            </span>
            <span className="profile-menu-label">{t("morePage.shareFeedback2")}</span>
            <ChevronRow />
          </button>

          <button
            type="button"
            className="profile-menu-row"
            onClick={() => {
              setHelpOpen(false);
              setTermsOpen(true);
            }}
          >
            <span className="profile-menu-ic" aria-hidden="true">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="6" y="3.5" width="12" height="17" rx="2"/><path d="M9.5 8.5h5"/><path d="M9.5 12h5"/><path d="M9.5 15.5h3"/></svg>
            </span>
            <span className="profile-menu-label">{t("morePage.terms")}</span>
            <ChevronRow />
          </button>

          {/* Straight to the mail app — the same address the feedback form's
              "Email us instead" uses. */}
          <a className="profile-menu-row" href={contactHref}>
            <span className="profile-menu-ic" aria-hidden="true">{HEADPHONES}</span>
            <span className="profile-menu-label">{t("morePage.contactUs")}</span>
            <ChevronRow />
          </a>

          {deletionInfo.status === "pending_delete" ? (
            <button
              type="button"
              className="profile-menu-row profile-menu-row--danger"
              onClick={cancelAccountDeletion}
              disabled={delBusy}
            >
              <span className="profile-menu-ic profile-menu-ic--danger" aria-hidden="true">{TRASH}</span>
              <span className="profile-menu-label">{t("morePage.cancelDeletion")}</span>
              <ChevronRow />
            </button>
          ) : (
            <button
              type="button"
              className="profile-menu-row profile-menu-row--danger"
              onClick={() => {
                setHelpOpen(false);
                setDelOpen(true);
              }}
              disabled={delBusy}
            >
              <span className="profile-menu-ic profile-menu-ic--danger" aria-hidden="true">{TRASH}</span>
              <span className="profile-menu-label">{t("morePage.deleteAccount")}</span>
              <ChevronRow />
            </button>
          )}
        </div>

        {deletionInfo.status === "pending_delete" && (
          <div className="profile-deletion-note">
            <T k="morePage.scheduledForDeletionOn" vars={{ value: deletionInfo?.scheduled ? new Date(deletionInfo.scheduled).toLocaleString() : "—" }} tags={{ 0: (c) => <strong>{c}</strong> }} />
          </div>
        )}
      </BottomSheet>

      {/* ── Terms drawer — the two legal documents. Delete account moved up to
          the "Help, terms & support" sheet (founder, round 4); its 7-day flow
          is unchanged. ── */}
      <BottomSheet
        open={termsOpen}
        title={t("morePage.terms")}
        onClose={() => {
          setTermsOpen(false);
          setHelpOpen(true); // back one level
        }}
      >
        <div className="profile-menu">
          <button type="button" className="profile-menu-row" onClick={() => navigate("/terms")}>
            <span className="profile-menu-ic" aria-hidden="true">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="6" y="3.5" width="12" height="17" rx="2"/><path d="M9.5 8.5h5"/><path d="M9.5 12h5"/><path d="M9.5 15.5h3"/></svg>
            </span>
            <span className="profile-menu-label">{t("morePage.termsConditions")}</span>
            <ChevronRow />
          </button>

          <button type="button" className="profile-menu-row" onClick={() => navigate("/privacy")}>
            <span className="profile-menu-ic" aria-hidden="true">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l7 3v5c0 4.5-3 8-7 9.5C8 19 5 15.5 5 11V6l7-3z"/></svg>
            </span>
            <span className="profile-menu-label">{t("morePage.privacyPolicy")}</span>
            <ChevronRow />
          </button>

        </div>

      </BottomSheet>

      {delOpen && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
          }}
          onClick={() => {
            if (delBusy) return;
            setDelOpen(false);
            setHelpOpen(true); // back one level
          }}
        >
          <div
            style={{
              background: "#111827", color: "#fff", border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 16, padding: 22, width: "min(92vw, 380px)", boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 800, fontSize: "1rem", marginBottom: 8 }}>{t("morePage.confirmDeletion")}</div>
            <div style={{ color: "#9ca3af", fontSize: 13, marginBottom: 18, lineHeight: 1.5 }}>
              <T k="morePage.yourAccountWillBe" tags={{ 0: (c) => <b style={{ color: "#fff" }}>{c}</b> }} />
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                onClick={() => {
                  setDelOpen(false);
                  setHelpOpen(true); // back one level
                }}
                disabled={delBusy}
                style={{
                  padding: "9px 16px", borderRadius: 10, background: "rgba(255,255,255,0.06)", color: "#fff",
                  border: "1px solid rgba(255,255,255,0.08)", cursor: "pointer", fontWeight: 600, fontSize: "0.85rem",
                }}
              >
                {t("morePage.cancel")}
              </button>
              <button
                onClick={requestAccountDeletion}
                disabled={delBusy}
                style={{
                  padding: "9px 16px", borderRadius: 10, background: "#ef4444", color: "#fff",
                  border: "none", cursor: "pointer", fontWeight: 600, fontSize: "0.85rem",
                }}
              >
                {delBusy ? t("morePage.scheduling") : t("morePage.delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MorePage;
